import { useEffect, useState } from "react"
import { database } from "@/firebase"
import type {
  DeviceFromApiType,
  DeviceFromFirebaseType,
  MergedDeviceType,
} from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { onValue, ref } from "firebase/database"

import { canViewThisPage } from "@/lib/api/auth"
import { getAllUserDevices } from "@/lib/api/location"
import { Loader } from "@/components/loader"
import { MapRenderer } from "@/components/map/map-libre/map-renderer"
import { SearchDialog } from "@/components/search-dialog"

interface UsersDataType {
  [userId: string]: {
    devices: Record<string, DeviceFromFirebaseType>
  }
}

export const Route = createFileRoute("/_home/tracking")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_tracking")) {
      return
    }
    return redirect({ to: "/access-denied" })
  },
  component: MapPage,
})

function MapPage() {
  const [markers, setMarkers] = useState<MergedDeviceType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedLocation, setSelectedLocation] = useState<
    [number, number] | null
  >(null)

  const { data: userDevices } = useQuery({
    queryKey: ["userDevices"],
    queryFn: getAllUserDevices,
  })

  useEffect(() => {
    const usersRef = ref(database, "users")
    const unsubscribe = onValue(
      usersRef,
      (snapshot) => {
        const rawUsers = snapshot.val()

        // ✅ Safely handle null/undefined or non-object values
        const usersData: UsersDataType =
          rawUsers && typeof rawUsers === "object" ? rawUsers : {}

        // Optional: filter out keys named "undefined" (if they exist)
        const filteredUsersData = Object.entries(usersData)
          .filter(([key]) => key !== "undefined")
          .reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined) {
              acc[key] = value
            }
            return acc
          }, {} as UsersDataType)

        const mergedDevices: MergedDeviceType[] = []

        // ✅ Only proceed if we have both users and devices data
        if (filteredUsersData && userDevices && Array.isArray(userDevices)) {
          const apiDevices = userDevices.filter((device) => device.is_active)
          const apiDeviceIds = new Set(
            apiDevices.map((device) => device.device_id),
          )

          Object.entries(filteredUsersData).forEach(([userId, user]) => {
            if (user?.devices) {
              Object.entries(user.devices).forEach(([deviceId, device]) => {
                if (apiDeviceIds.has(deviceId)) {
                  const matchingApiDevice = apiDevices.find(
                    (apiDevice) => apiDevice.device_id === deviceId,
                  )
                  if (matchingApiDevice) {
                    mergedDevices.push({
                      ...matchingApiDevice,
                      ...device,
                    })
                  }
                }
              })
            }
          })
        }

        setMarkers(mergedDevices)
        setIsLoading(false)
      },
      (error) => {
        console.error("Firebase error:", error)
        setIsLoading(false)
      },
    )

    return () => unsubscribe()
  }, [userDevices])

  const handleDeviceSelect = (device: MergedDeviceType) => {
    setSelectedLocation([device.latitude, device.longitude])
  }

  // Example KMZ file – replace with real data from API if needed
  const kmzFiles = [
    {
      id: 1,
      name: "MDW-DMA-1.kmz",
      s3_url:
        "https://himo-files.s3.ap-southeast-1.amazonaws.com/KMZ+Files/Metro+Dumaguete+WD/SM+MOA+.kmz",
      version: 1,
      description: "test",
      is_active: true,
    },
  ]

  return (
    <main className="relative">
      {isLoading ? (
        <div className="flex h-screen items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="relative h-screen">
          <MapRenderer
            trackMarkers={markers}
            selectedLocation={selectedLocation}
            kmzFiles={kmzFiles}
          />
          <div className="absolute left-0 top-0 z-[500] p-2 md:p-4">
            <SearchDialog
              devices={markers}
              onDeviceSelect={handleDeviceSelect}
            />
          </div>
        </div>
      )}
    </main>
  )
}
